import { Injectable, inject } from '@angular/core';
import {ApiCisternContentDataResponseDto, CisternsApiService, TimeInterval} from '@birco/rainmesh-api';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  Subscription,
  switchMap
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SensorDataService {
  private cisternsApiService = inject(CisternsApiService);

  private subscription = new Subscription();

  private filter$$ = new BehaviorSubject<SensorDataFilter>({
    cisternId: null,
    startDate: new Date().toISOString(),
    interval: TimeInterval.MONTHLY
  });
  private loading$$ = new BehaviorSubject<boolean>(false);
  private data$$ = new BehaviorSubject<ApiCisternContentDataResponseDto | null>(null);

  constructor() {
    this.subscription.add(this.initDataSubscription());
  }

  getData$(): Observable<{ data: ApiCisternContentDataResponseDto | null, filter: SensorDataFilter }> {
    return combineLatest([this.data$$, this.filter$$]).pipe(
      map(([data, filter]) => ({data, filter}))
    );
  }

  getLoading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  changeFilter(filter: SensorDataFilter) {
    this.filter$$.next(filter);
  }

  getDataForFilter(filter: SensorDataFilter): Observable<{ data: ApiCisternContentDataResponseDto | null; filter: SensorDataFilter }> {
    return this.loadData(filter).pipe(
      map(data => ({data, filter}))
    );
  }

  private initDataSubscription(): Subscription {
    return this.filter$$.pipe(
      filter(filter => !!filter),
      distinctUntilChanged((a, b) => {
        return a.cisternId === b.cisternId &&
          a.startDate === b.startDate &&
          a.interval === b.interval;
      }),
      switchMap(filter => this.loadData(filter, true))
    ).subscribe(data => this.data$$.next(data));
  }

  private loadData(filter: SensorDataFilter, updateLoading = false): Observable<ApiCisternContentDataResponseDto | null> {
    if (!filter.cisternId) {
      return of(null);
    }

    if (updateLoading) {
      this.loading$$.next(true);
    }

    return this.cisternsApiService.getCisternContentData(filter.cisternId, filter.startDate, filter.interval)
      .pipe(
        catchError(error => {
          console.error('Fehler beim Laden der Daten:', error);
          return of(null);
        }),
        finalize(() => {
          if (updateLoading) {
            this.loading$$.next(false);
          }
        })
      );
  }
}

export interface SensorDataFilter {
  cisternId: string | null,
  startDate: string,
  interval: TimeInterval
}
